"use client";

import { ReactNode, Suspense, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { Header } from "./Header";
import { useSectionSnapScroll } from "@/hooks/useSectionSnapScroll";

const Footer = dynamic(
  () => import("./Footer").then((m) => ({ default: m.Footer })),
  { ssr: true }
);
const CookieConsent = dynamic(
  () =>
    import("@/components/CookieConsent").then((m) => ({
      default: m.CookieConsent,
    })),
  { ssr: false }
);
const GoogleAnalytics = dynamic(
  () =>
    import("@/components/GoogleAnalytics").then((m) => ({
      default: m.GoogleAnalytics,
    })),
  { ssr: false }
);
const SnapRail = dynamic(
  () => import("./SnapRail").then((m) => ({ default: m.SnapRail })),
  { ssr: false }
);

interface LayoutProps {
  children: ReactNode;
  showFooter?: boolean;
  enableSnap?: boolean;
  showSnapRail?: boolean;
}

export function Layout({
  children,
  showFooter = true,
  enableSnap = true,
  showSnapRail,
}: LayoutProps) {
  const pathname = usePathname();
  const [snapSectionIds, setSnapSectionIds] = useState<string[]>([]);
  const [isDesktopSnap, setIsDesktopSnap] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [showDeferredUi, setShowDeferredUi] = useState(false);
  // Desktop centered scroll effect disabled on request.
  // To restore the previous behavior, set this back to:
  // const desktopCenteredSnapEnabled = enableSnap && isDesktopSnap;
  const desktopCenteredSnapEnabled = false;
  // Keep right-side rail visible on desktop even with centered snap disabled.
  const shouldShowSnapRail = showSnapRail ?? (enableSnap && isDesktopSnap);

  useSectionSnapScroll(snapSectionIds, {
    // Previous behavior (commented on purpose):
    // enabled: enableSnap && isDesktopSnap && snapSectionIds.length > 1,
    enabled: desktopCenteredSnapEnabled && snapSectionIds.length > 1,
    mobileBreakpoint: 1280,
  });

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);

    return () => {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "auto";
      }
    };
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1280px)");
    const onChange = () => setIsDesktopSnap(mediaQuery.matches);
    onChange();
    mediaQuery.addEventListener("change", onChange);

    return () => mediaQuery.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const onChange = () => setIsMobileViewport(mediaQuery.matches);
    onChange();
    mediaQuery.addEventListener("change", onChange);

    return () => mediaQuery.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!showDeferredUi && !desktopCenteredSnapEnabled) {
      return;
    }

    // Defer non-critical UI styles (snap rail + optional mono font face).
    void import("@/styles/deferred-ui.css");
  }, [showDeferredUi, desktopCenteredSnapEnabled]);

  useEffect(() => {
    // Previous behavior (commented on purpose):
    // if (enableSnap && isDesktopSnap) {
    if (desktopCenteredSnapEnabled) {
      document.documentElement.classList.add("has-site-snap");
      document.body.classList.add("has-site-snap");
      return () => {
        document.documentElement.classList.remove("has-site-snap");
        document.body.classList.remove("has-site-snap");
      };
    }

    document.documentElement.classList.remove("has-site-snap");
    document.body.classList.remove("has-site-snap");
    return () => {
      document.documentElement.classList.remove("has-site-snap");
      document.body.classList.remove("has-site-snap");
    };
  }, [desktopCenteredSnapEnabled]);

  useEffect(() => {
    let cancelled = false;
    let idleId: number | null = null;
    let timeoutId: number | null = null;
    const requestIdle = window.requestIdleCallback?.bind(window);
    const cancelIdle = window.cancelIdleCallback?.bind(window);
    let cleanupInteractions: (() => void) | null = null;

    const revealDeferredUi = () => {
      if (!cancelled) {
        setShowDeferredUi(true);
      }
    };

    if (isMobileViewport) {
      const interactionEvents: Array<keyof WindowEventMap> = [
        "scroll",
        "touchstart",
        "pointerdown",
        "keydown",
      ];
      const handleInteraction = () => {
        revealDeferredUi();
        interactionEvents.forEach((eventName) => {
          window.removeEventListener(eventName, handleInteraction);
        });
      };

      interactionEvents.forEach((eventName) => {
        window.addEventListener(eventName, handleInteraction, {
          passive: true,
          once: true,
        });
      });

      cleanupInteractions = () => {
        interactionEvents.forEach((eventName) => {
          window.removeEventListener(eventName, handleInteraction);
        });
      };

      timeoutId = window.setTimeout(revealDeferredUi, 4200);
    } else {
      const interactionEvents: Array<keyof WindowEventMap> = [
        "scroll",
        "pointerdown",
        "keydown",
        "mousemove",
      ];
      const handleInteraction = () => {
        revealDeferredUi();
        interactionEvents.forEach((eventName) => {
          window.removeEventListener(eventName, handleInteraction);
        });
      };

      interactionEvents.forEach((eventName) => {
        window.addEventListener(eventName, handleInteraction, {
          passive: true,
          once: true,
        });
      });

      cleanupInteractions = () => {
        interactionEvents.forEach((eventName) => {
          window.removeEventListener(eventName, handleInteraction);
        });
      };

      if (requestIdle) {
        idleId = requestIdle(revealDeferredUi, { timeout: 4800 });
      } else {
        timeoutId = window.setTimeout(revealDeferredUi, 4800);
      }
    }

    return () => {
      cancelled = true;
      if (idleId !== null && cancelIdle) {
        cancelIdle(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      cleanupInteractions?.();
    };
  }, [isMobileViewport]);

  useEffect(() => {
    const rafId = window.requestAnimationFrame(() => {
      const pathKey = (pathname ?? "/")
        .replace(/\//g, "-")
        .replace(/^-+|-+$/g, "") || "home";

      const sectionIds = Array.from(
        document.querySelectorAll("main > section")
      ).map((section, index) => {
        const node = section as HTMLElement;
        if (!node.id) {
          node.id = `snap-${pathKey}-${index + 1}`;
        }
        return node.id;
      });

      if (showFooter && showDeferredUi) {
        const footer = document.getElementById("site-footer");
        if (footer?.id) {
          sectionIds.push(footer.id);
        }
      }

      setSnapSectionIds(sectionIds);
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [pathname, showFooter, showDeferredUi, children]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="relative flex-1 overflow-x-clip pt-20 md:pt-24">
        {children}
      </main>
      {/* Footer in the server HTML: service links, P.IVA, PEC and legal pages
          must exist for crawlers that don't run JS. Its WebGL cube already
          loads only when scrolled into view (FooterHypercube). */}
      {showFooter ? <Footer /> : null}
      {showDeferredUi ? (
        <Suspense fallback={null}>
          {shouldShowSnapRail ? <SnapRail /> : null}
          <CookieConsent />
          <GoogleAnalytics />
        </Suspense>
      ) : null}
    </div>
  );
}
